import { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import InvoiceReminderTemplate from "../../components/InvoiceReminderTemplate";
import PortalPopup from "../../components/PortalPopup";
import NavigationPanel from "../../components/NavigationPanel";
import Toggle from "../../components/Toggle";
import SettingNav from "../../components/settingNav";
import Breadcrumb from "../../components/Breadcrumb";
import Footer from "../../components/Footer";
import ToastMsg from "../../components/ToastMsg";
import invoiceReminderService from "../../services/invoiceReminderService";

const SettingsInvoiceReminderPage = () => {
  const [isInvoiceReminderTemplateOpen, setInvoiceReminderTemplateOpen] =
    useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [enableReminders, setEnableReminders] = useState(true);
  const location = useLocation();
  const [settingsId, setSettingsId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [templateContent, setTemplateContent] = useState("");
  const handleTemplateSave = (content) => {
    setTemplateContent(content);
    console.log("Content", content);
  };
  const userId =
    localStorage.getItem("userId") || sessionStorage.getItem("userId");

  // State for checkboxes - 10 days is set to true by default
  const [whenToSendOptions, setWhenToSendOptions] = useState({
    tenDays: true, // Default to checked
    fiveDays: false,
    oneDay: false,
  });

  const [applyToOptions, setApplyToOptions] = useState({
    allClients: true,
    selectedGroups: false,
  });

  const [channelsEnabled, setChannelsEnabled] = useState({
    email: true,
    sms: true,
    push: true,
  });

  const [overridesOptions, setOverridesOptions] = useState({
    optOut: true,
    overdueOnly: false,
    pauseHolidays: true,
  });
  useEffect(() => {
    loadReminderSettings();
  }, []);

  const loadReminderSettings = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await invoiceReminderService.getReminderSettings();

      if (result.success && result.data) {
        const settings = result.data;

        // Update all state with loaded settings
        setEnableReminders(settings.enableReminders ?? true);
        setSettingsId(settings._id || settings.id); // Store the ID for updates

        if (settings.whenToSend) {
          setWhenToSendOptions({
            tenDays: settings.whenToSend.tenDays ?? true,
            fiveDays: settings.whenToSend.fiveDays ?? false,
            oneDay: settings.whenToSend.oneDay ?? false,
          });
        }

        if (settings.applyTo) {
          setApplyToOptions({
            allClients: settings.applyTo.allClients ?? true,
            selectedGroups: settings.applyTo.selectedGroups ?? false,
          });
        }

        if (settings.channels) {
          setChannelsEnabled({
            email: settings.channels.email ?? true,
            sms: settings.channels.sms ?? true,
            push: settings.channels.push ?? true,
          });
        }

        if (settings.overrides) {
          setOverridesOptions({
            optOut: settings.overrides.optOut ?? true,
            overdueOnly: settings.overrides.overdueOnly ?? false,
            pauseHolidays: settings.overrides.pauseHolidays ?? true,
          });
        }
      }
    } catch (err) {
      setError("Failed to load reminder settings");
      console.error("Error loading settings:", err);
    } finally {
      setIsLoading(false);
    }
  };
  const [isToastMsgOpen, setToastMsgOpen] = useState(false);

  const openToastMsg = useCallback(() => {
    setToastMsgOpen(true);
  }, []);

  const closeToastMsg = useCallback(() => {
    setToastMsgOpen(false);
  }, []);

  const saveReminderSettings = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const selectedWhenToSend = Object.keys(whenToSendOptions || {}).filter(
        (key) => whenToSendOptions[key],
      );
      const selectedApplyTo = Object.keys(applyToOptions || {}).filter(
        (key) => applyToOptions[key],
      );
      const selectedChannels = Object.keys(channelsEnabled || {}).filter(
        (key) => channelsEnabled[key],
      );

      const settings = {
        userId,
        enableReminders,
        whenToSend: selectedWhenToSend,
        applyTo: selectedApplyTo,
        channels: selectedChannels,
        overrides: overridesOptions || {},
        content: templateContent, // required by schema
        updatedAt: new Date().toISOString(),
      };

      let result;
      if (settingsId) {
        result = await invoiceReminderService.updateReminderSettings(
          settingsId,
          settings,
        );
      } else {
        result = await invoiceReminderService.saveReminderSettings(settings);
      }

      if (result.success) {
        if (result.data && result.data._id && !settingsId) {
          setSettingsId(result.data._id);
        }
        console.log("Settings saved successfully!");
        openToastMsg();
      } else {
        throw new Error(result.error || "Failed to save settings");
      }
    } catch (err) {
      setError(err.message || "Failed to save reminder settings");
      console.error("Error saving settings:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const openInvoiceReminderTemplate = useCallback(() => {
    setInvoiceReminderTemplateOpen(true);
  }, []);

  const closeInvoiceReminderTemplate = useCallback(() => {
    setInvoiceReminderTemplateOpen(false);
  }, []);

  const openLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(true);
  }, []);

  const onTabStatesContainerClick = useCallback(() => {
    // Add your code here
  }, []);

  const handleToggle = (state) => {
    console.log("Toggle state is:", state);
  };

  // Handler functions for checkboxes
  const handleWhenToSendChange = (option) => {
    setWhenToSendOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleApplyToChange = (option) => {
    setApplyToOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleChannelsChange = (option) => {
    setChannelsEnabled((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleOverridesChange = (option) => {
    setOverridesOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  // Get the selected days as an array
  const getSelectedDays = () => {
    const selectedDays = [];
    if (whenToSendOptions.tenDays) selectedDays.push(10);
    if (whenToSendOptions.fiveDays) selectedDays.push(5);
    if (whenToSendOptions.oneDay) selectedDays.push(1);
    return selectedDays;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }

        .dd-root * { box-sizing: border-box; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }
        .dd-root.bg-white,
        .dd-root.bg-neutrals-neutral-50,
        .dd-root.bg-gray-50 {
          background: rgb(241, 245, 249) !important;
        }

        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .dd-section-divider {
          border: none;
          border-top: 1px solid #E2E8F0;
          margin: 0;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin: 0 0 12px 0;
        }

        .dd-section-sublabel {
          font-size: 13px;
          color: #64748B;
          margin: 2px 0 0 0;
        }

        .dd-checkbox-row {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          color: #1E293B;
        }

        .dd-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #CBD5E1;
          accent-color: #2563EB;
          cursor: pointer;
          flex-shrink: 0;
        }
        .dd-checkbox:disabled { opacity: 0.4; cursor: not-allowed; }

        .dd-info-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94A3B8;
        }

        .dd-btn-secondary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s;
        }
        .dd-btn-secondary:hover { border-color: #94A3B8; background: #F8FAFC; }

        .dd-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #2563EB; border: 1px solid #2563EB; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #ffffff;
          cursor: pointer;
          transition: background 0.15s, box-shadow 0.15s;
        }
        .dd-btn-primary:hover { background: #1D4ED8; box-shadow: 0 4px 12px rgba(37,99,235,0.25); }
        .dd-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-edit-link {
          font-size: 13px; font-weight: 600; color: #2563EB;
          cursor: pointer; text-decoration: none;
        }
        .dd-edit-link:hover { text-decoration: underline; }
      `}</style>

      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        {/* Navigation Panel */}
        <NavigationPanel />

        {/* Main */}
        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Page heading */}
          <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 24 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              Settings
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Get your billing information, generate/review invoices.
            </p>
          </div>

          {/* Body: sidenav + content */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>
            {/* Tabs */}
            <SettingNav />

            {/* Content card */}
            <div
              className="dd-card"
              style={{
                flex: 1,
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              {/* Card header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  paddingBottom: 20,
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                  >
                    Invoice reminder
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                    Set up automated reminders for upcoming and overdue invoices
                    across all clients.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button className="dd-btn-secondary">Cancel</button>
                  <button
                    className="dd-btn-primary"
                    onClick={saveReminderSettings}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>

              {/* Enable reminders */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <p className="dd-section-label" style={{ margin: 0 }}>
                    Enable reminders
                  </p>
                  <Toggle
                    onToggle={handleToggle}
                    isOn={enableReminders}
                    onColor="bg-primary-primary-500"
                    offColor="bg-primary-primary-50"
                    knobColor="bg-neutrals-neutral-50"
                  />
                </div>

                <hr className="dd-section-divider" />

                {/* When to send */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  <p className="dd-section-label">When to send</p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {[
                      { key: "tenDays", label: "10 days before due date" },
                      { key: "fiveDays", label: "5 days before due date" },
                      { key: "oneDay", label: "1 day before due date" },
                    ].map(({ key, label }) => (
                      <label key={key} className="dd-checkbox-row">
                        <input
                          type="checkbox"
                          className="dd-checkbox"
                          checked={whenToSendOptions[key]}
                          onChange={() => handleWhenToSendChange(key)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                <hr className="dd-section-divider" />

                {/* Apply to */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  <p className="dd-section-label">Apply to</p>
                  <div style={{ display: "flex", gap: 24 }}>
                    {[
                      {
                        key: "allClients",
                        label: "All clients",
                        disabled: false,
                      },
                      {
                        key: "selectedGroups",
                        label: "Selected client groups",
                        disabled: true,
                      },
                    ].map(({ key, label, disabled }) => (
                      <label key={key} className="dd-checkbox-row">
                        <input
                          type="checkbox"
                          className="dd-checkbox"
                          checked={applyToOptions[key]}
                          disabled={disabled}
                          onChange={() => handleApplyToChange(key)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Info hint */}
                <div className="dd-info-row">
                  <img
                    src="assets/info.svg"
                    alt=""
                    style={{ width: 14, height: 14, opacity: 0.6 }}
                  />
                  Rules apply to all future invoices once saved.
                </div>
              </div>

              <hr className="dd-section-divider" />

              {/* Templates */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <p className="dd-section-label">Templates</p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontSize: 14,
                    color: "#1E293B",
                  }}
                >
                  Email reminder
                  <span
                    className="dd-edit-link"
                    onClick={openInvoiceReminderTemplate}
                  >
                    Edit
                  </span>
                </div>
              </div>

              <hr className="dd-section-divider" />

              {/* Overrides */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <p className="dd-section-label">Overrides and exceptions</p>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {[
                    {
                      key: "optOut",
                      label: "Allow clients to opt-out of reminders",
                    },
                    {
                      key: "overdueOnly",
                      label: "Only send reminders for overdue invoices",
                    },
                  ].map(({ key, label }) => (
                    <label key={key} className="dd-checkbox-row">
                      <input
                        type="checkbox"
                        className="dd-checkbox"
                        checked={overridesOptions[key]}
                        onChange={() => handleOverridesChange(key)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {isInvoiceReminderTemplateOpen && (
        <PortalPopup
          overlayColor="rgba(0, 0, 0, 0.25)"
          placement="Centered"
          onOutsideClick={closeInvoiceReminderTemplate}
        >
          <InvoiceReminderTemplate
            onClose={closeInvoiceReminderTemplate}
            onSave={handleTemplateSave}
          />
        </PortalPopup>
      )}
      {isToastMsgOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeToastMsg}
        >
          <ToastMsg onClose={closeToastMsg} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsInvoiceReminderPage;
